import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../../../components/ErrorBoundary';
import {
	ConciliacaoItemInfo,
	ConciliacaoFundoDetalhes,
	fetchConciliacaoDetalhes,
} from '../conciliacao-detalhe';
import DataTable, { ColumnDef } from '../../../components/DataTable';
import FiltersCard from '../../../components/FiltersCard/FiltersCard';
import Pagination from '../../../components/Pagination/Pagination';
import Button from '../../../components/Button/Button';
import Input from '../../../components/input/Input';
import { API_CONFIG } from '../../../config/api';
import { authService } from '../../../services/authService';
import './style.css';
import '../../../styles/filters-card.css';

interface ConciliacaoItem {
	carteira: string;
	cnpjFundo: string;
	saldoEstoque: string;
	saldoRegistradora: string;
	qtdParcelasEstoque: number;
	qtdParcelasRegistradora: number;
	diferencaEstoqueRegistradora: string;
	responsavel: string;
	ultimaConciliacao: string;
	idUltimaConciliacao: string;
}

interface ConciliacoesResponse {
	content: ConciliacaoItem[];
	pageNumber: number;
	pageSize: number;
	totalElements: number;
	totalPages: number;
	first: boolean;
	last: boolean;
	hasNext: boolean;
}

interface ConciliacoesFetchResult {
	items: ConciliacaoItem[];
	totalElements: number;
}

interface FiltrosConciliacao {
	carteira: string;
	responsavel: string;
}

interface ConciliacoesGeraisPersistedState {
	filtros: FiltrosConciliacao;
	currentPage: number;
	pageSize: number;
	sortField: string;
	sortDirection: 'asc' | 'desc';
	data: ConciliacaoItem[];
	selectedKeys: string[];
}

const CONCILIACOES_GERAIS_STORAGE_KEY = 'conciliacoes_gerais_filters_state';
const MAX_CONCILIACOES_SELECIONADAS = 3;

const loadPersistedState = (): ConciliacoesGeraisPersistedState | null => {
	try {
		const rawValue = sessionStorage.getItem(CONCILIACOES_GERAIS_STORAGE_KEY);
		if (!rawValue) return null;

		const parsed = JSON.parse(rawValue) as Partial<ConciliacoesGeraisPersistedState>;
		if (!parsed || typeof parsed !== 'object') return null;

		return {
			filtros: {
				carteira: parsed.filtros?.carteira || '',
				responsavel: parsed.filtros?.responsavel || '',
			},
			currentPage: typeof parsed.currentPage === 'number' ? parsed.currentPage : 0,
			pageSize: typeof parsed.pageSize === 'number' ? parsed.pageSize : 10,
			sortField: typeof parsed.sortField === 'string' ? parsed.sortField : '',
			sortDirection: parsed.sortDirection === 'desc' ? 'desc' : 'asc',
			data: Array.isArray(parsed.data) ? parsed.data : [],
			selectedKeys: Array.isArray(parsed.selectedKeys) ? parsed.selectedKeys : [],
		};
	} catch {
		return null;
	}
};

const parsePtBrNumber = (value?: string | number) => {
	if (typeof value === 'number') return value;
	if (!value) return NaN;

	const normalized = value
		.replace(/\s/g, '')
		.replace(/\./g, '')
		.replace(',', '.')
		.replace(/[^0-9.-]/g, '');

	return Number(normalized);
};

const formatCurrency = (value?: string | number) => {
	const parsed = parsePtBrNumber(value);
	if (Number.isNaN(parsed)) return '-';

	return new Intl.NumberFormat('pt-BR', {
		style: 'currency',
		currency: 'BRL',
	}).format(parsed);
};

const formatNumber = (value?: number) => {
	if (typeof value !== 'number') return '-';
	return new Intl.NumberFormat('pt-BR').format(value);
};

const formatDateTime = (value?: string) => {
	if (!value) return '-';

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '-';

	return new Intl.DateTimeFormat('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	}).format(date);
};

const sortString = (value?: string) => (value || '').toLowerCase();

const getSortValue = (item: ConciliacaoItem, field: string): string | number => {
	switch (field) {
		case 'saldoEstoque':
			return parsePtBrNumber(item.saldoEstoque);
		case 'saldoRegistradora':
			return parsePtBrNumber(item.saldoRegistradora);
		case 'diferencaEstoqueRegistradora':
			return parsePtBrNumber(item.diferencaEstoqueRegistradora);
		case 'qtdParcelasEstoque':
			return item.qtdParcelasEstoque;
		case 'qtdParcelasRegistradora':
			return item.qtdParcelasRegistradora;
		case 'ultimaConciliacao': {
			const timestamp = new Date(item.ultimaConciliacao).getTime();
			return Number.isNaN(timestamp) ? 0 : timestamp;
		}
		case 'carteira':
			return sortString(item.carteira);
		case 'cnpjFundo':
			return sortString(item.cnpjFundo);
		case 'responsavel':
			return sortString(item.responsavel);
		default:
			return sortString((item as unknown as Record<string, string | number | undefined>)[field]?.toString());
	}
};

type ConciliacaoLoadStatus = 'loading' | 'done' | 'error';

interface ConciliacaoLoad {
	key: string;
	items: ConciliacaoItemInfo[];
	status: ConciliacaoLoadStatus;
	data: ConciliacaoFundoDetalhes[] | null;
	error: string | null;
}

let currentLoad: ConciliacaoLoad | null = null;
const loadListeners = new Set<() => void>();

const emitLoadChange = () => {
	loadListeners.forEach((listener) => listener());
};

const subscribeLoad = (listener: () => void) => {
	loadListeners.add(listener);
	return () => {
		loadListeners.delete(listener);
	};
};

const getLoadSnapshot = () => currentLoad;

const selectionKey = (cnpjs: string[]) => cnpjs.join(',');

const startConciliacaoLoad = (items: ConciliacaoItemInfo[]) => {
	const cnpjs = items.map((it) => it.cnpjFundo);
	const key = selectionKey(cnpjs);

	currentLoad = { key, items, status: 'loading', data: null, error: null };
	emitLoadChange();

	fetchConciliacaoDetalhes(cnpjs)
		.then((data) => {
			// Ignora se um novo carregamento (outra seleção) começou enquanto isso.
			if (currentLoad?.key !== key) return;
			currentLoad = { key, items, status: 'done', data, error: null };
			emitLoadChange();
		})
		.catch((err) => {
			if (currentLoad?.key !== key) return;
			currentLoad = {
				key,
				items,
				status: 'error',
				data: null,
				error: err instanceof Error ? err.message : 'Erro ao iniciar conciliação',
			};
			emitLoadChange();
		});
};

const clearConciliacaoLoad = () => {
	if (!currentLoad) return;
	currentLoad = null;
	emitLoadChange();
};

const ConciliacoesGerais = () => {
	const navigate = useNavigate();
	const persistedState = loadPersistedState();

	const [selectedKeys, setSelectedKeys] = useState<string[]>(persistedState?.selectedKeys || []);

	const load = useSyncExternalStore(subscribeLoad, getLoadSnapshot);
	// Marca que ESTA montagem iniciou o carregamento — só ela abre automaticamente
	// ao concluir. Se o usuário saiu e voltou, abre manualmente via botão "Abrir".
	const awaitingOpenRef = useRef(false);

	const [filtros, setFiltros] = useState<FiltrosConciliacao>(
		persistedState?.filtros || {
			carteira: '',
			responsavel: '',
		}
	);

	const [data, setData] = useState<ConciliacaoItem[]>(persistedState?.data || []);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [currentPage, setCurrentPage] = useState(persistedState?.currentPage || 0);
	const [pageSize, setPageSize] = useState(persistedState?.pageSize || 10);

	const [sortField, setSortField] = useState(persistedState?.sortField || '');
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(persistedState?.sortDirection || 'asc');

	useEffect(() => {
		sessionStorage.setItem(
			CONCILIACOES_GERAIS_STORAGE_KEY,
			JSON.stringify({
				filtros,
				currentPage,
				pageSize,
				sortField,
				sortDirection,
				data,
				selectedKeys,
			} satisfies ConciliacoesGeraisPersistedState)
		);
	}, [filtros, currentPage, pageSize, sortField, sortDirection, data, selectedKeys]);

	const fetchConciliacoes = useCallback(
		async (): Promise<ConciliacoesFetchResult> => {
			setLoading(true);
			setError(null);

			try {
				const token = await authService.getValidToken();
				let page = 0;
				let hasNext = true;
				let totalElements = 0;
				const allItems: ConciliacaoItem[] = [];

				while (hasNext) {
					const params = new URLSearchParams();
					params.append('page', page.toString());
					params.append('size', '200');

					if (filtros.carteira.trim()) {
						params.append('carteira', filtros.carteira.trim());
					}

					if (filtros.responsavel.trim()) {
						params.append('responsavel', filtros.responsavel.trim());
					}

					const response = await fetch(`${API_CONFIG.BASE_URL_JAVA}/api/v1/conciliacoes?${params.toString()}`, {
						method: 'GET',
						credentials: 'include',
						headers: {
							'Content-Type': 'application/json',
							Authorization: `Bearer ${token}`,
						},
					});

					if (!response.ok) {
						let errorMessage = `Erro ao buscar conciliações: ${response.statusText}`;
						try {
							const errorBody = await response.json();
							if (errorBody?.message) {
								errorMessage = errorBody.message;
							}
						} catch {
							// no-op
						}
						throw new Error(errorMessage);
					}

					const result: ConciliacoesResponse = await response.json();
					allItems.push(...(result.content || []));
					totalElements = result.totalElements || allItems.length;
					hasNext = result.hasNext;
					page += 1;
				}

				return {
					items: allItems,
					totalElements,
				};
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Erro ao carregar conciliações';
				setError(message);
				return {
					items: [],
					totalElements: 0,
				};
			} finally {
				setLoading(false);
			}
		},
		[filtros]
	);

	const sortedData = useMemo(() => {
		if (!sortField) {
			return data;
		}

		const sorted = [...data].sort((a, b) => {
			const aValue = getSortValue(a, sortField);
			const bValue = getSortValue(b, sortField);

			if (aValue === bValue) return 0;

			if (sortDirection === 'asc') {
				return aValue > bValue ? 1 : -1;
			}

			return aValue < bValue ? 1 : -1;
		});

		return sorted;
	}, [data, sortField, sortDirection]);

	const totalElements = sortedData.length;
	const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

	const paginatedData = useMemo(() => {
		const start = currentPage * pageSize;
		const end = start + pageSize;
		return sortedData.slice(start, end);
	}, [sortedData, currentPage, pageSize]);

	useEffect(() => {
		if (currentPage > totalPages - 1) {
			setCurrentPage(Math.max(totalPages - 1, 0));
		}
	}, [currentPage, totalPages]);

	const columns = useMemo<ColumnDef<ConciliacaoItem>[]>(
		() => [
			{ key: 'carteira', label: 'Carteira', sortable: true },
			{ key: 'cnpjFundo', label: 'CNPJ Fundo', sortable: true },
			{
				key: 'saldoEstoque',
				label: 'Saldo Estoque',
				sortable: true,
				render: (item) => formatCurrency(item.saldoEstoque),
			},
			{
				key: 'saldoRegistradora',
				label: 'Saldo Registradora',
				sortable: true,
				render: (item) => formatCurrency(item.saldoRegistradora),
			},
			{
				key: 'qtdParcelasEstoque',
				label: 'Qtd. Parcelas Estoque',
				sortable: true,
				render: (item) => formatNumber(item.qtdParcelasEstoque),
			},
			{
				key: 'qtdParcelasRegistradora',
				label: 'Qtd. Parcelas Registradora',
				sortable: true,
				render: (item) => formatNumber(item.qtdParcelasRegistradora),
			},
			{
				key: 'diferencaEstoqueRegistradora',
				label: 'Diferença',
				sortable: true,
				render: (item) => formatCurrency(item.diferencaEstoqueRegistradora),
			},
			{ key: 'responsavel', label: 'Responsável', sortable: true },
			{
				key: 'ultimaConciliacao',
				label: 'Última Conciliação',
				sortable: true,
				render: (item) => formatDateTime(item.ultimaConciliacao),
			},
		],
		[]
	);

	const handleFilterChange = (field: keyof FiltrosConciliacao, value: string) => {
		setFiltros((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleClearFilters = () => {
		setFiltros({
			carteira: '',
			responsavel: '',
		});
		setData([]);
		setSelectedKeys([]);
		setCurrentPage(0);
		setSortField('');
		setSortDirection('asc');
	};

	const handleSearch = async () => {
		setCurrentPage(0);
		setSelectedKeys([]);
		const result = await fetchConciliacoes();
		setData(result.items);
	};

	const handleSort = (field: string) => {
		if (sortField === field) {
			setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
		setCurrentPage(0);
	};

	const getRowId = (item: ConciliacaoItem) => `${item.carteira}__${item.cnpjFundo}`;

	const isSelected = (item: ConciliacaoItem) => selectedKeys.includes(getRowId(item));
	const selectionFull = selectedKeys.length >= MAX_CONCILIACOES_SELECIONADAS;

	const toggleSelect = (item: ConciliacaoItem) => {
		const key = getRowId(item);
		setSelectedKeys((prev) => {
			if (prev.includes(key)) {
				return prev.filter((k) => k !== key);
			}
			if (prev.length >= MAX_CONCILIACOES_SELECIONADAS) {
				return prev;
			}
			return [...prev, key];
		});
	};

	const toItemInfo = (item: ConciliacaoItem): ConciliacaoItemInfo => ({
		carteira: item.carteira,
		cnpjFundo: item.cnpjFundo,
		responsavel: item.responsavel,
		ultimaConciliacao: item.ultimaConciliacao,
		idUltimaConciliacao: item.idUltimaConciliacao,
		qtdParcelasEstoque: item.qtdParcelasEstoque,
		qtdParcelasRegistradora: item.qtdParcelasRegistradora,
	});

	const selectedItensInfo = data
		.filter((item) => selectedKeys.includes(getRowId(item)))
		.map(toItemInfo);
	const currentSelectionKey = selectionKey(selectedItensInfo.map((it) => it.cnpjFundo));

	const loadMatchesSelection = !!load && load.key === currentSelectionKey;
	const isLoadingSelection = loadMatchesSelection && load!.status === 'loading';
	const isLoadedSelection = loadMatchesSelection && load!.status === 'done';

	const abrirConciliacao = useCallback(
		(itens: ConciliacaoItemInfo[], initialData: ConciliacaoFundoDetalhes[]) => {
			clearConciliacaoLoad();
			navigate('/conciliacoes/gerais/detalhes', { state: { items: itens, initialData } });
		},
		[navigate]
	);

	// Abre automaticamente quando ESTA montagem iniciou o carregamento e ele terminou.
	useEffect(() => {
		if (!awaitingOpenRef.current || !load || load.key !== currentSelectionKey) return;

		if (load.status === 'done' && load.data) {
			awaitingOpenRef.current = false;
			abrirConciliacao(load.items, load.data);
		} else if (load.status === 'error') {
			awaitingOpenRef.current = false;
			setError(load.error);
			clearConciliacaoLoad();
		}
	}, [load, currentSelectionKey, abrirConciliacao]);

	const handleConciliarSelecionadas = () => {
		if (!selectedItensInfo.length) return;

		// Já carregado para esta seleção (ex.: voltou de outra tela): abre direto.
		if (isLoadedSelection && load?.data) {
			abrirConciliacao(selectedItensInfo, load.data);
			return;
		}

		if (isLoadingSelection) return;

		setError(null);
		awaitingOpenRef.current = true;
		startConciliacaoLoad(selectedItensInfo);
	};

	const selectColumn: ColumnDef<ConciliacaoItem> = {
		key: 'select',
		label: '',
		headerClassName: 'conciliacao-select-col',
		cellClassName: 'conciliacao-select-col',
		render: (item) => {
			const checked = isSelected(item);
			const disabled = isLoadingSelection || (!checked && selectionFull);
			return (
				<input
					type="checkbox"
					checked={checked}
					disabled={disabled}
					onChange={() => toggleSelect(item)}
					title={
						disabled
							? `Máximo de ${MAX_CONCILIACOES_SELECIONADAS} conciliações por vez`
							: 'Selecionar conciliação'
					}
				/>
			);
		},
	};

	return (
		<ErrorBoundary>
			<div className="conciliacoes-gerais-container">
				<div className="page-header">
					<div className="page-title-section">
						<h1 className="page-title">Conciliações Gerais</h1>
						<p className="page-subtitle">
							Consulta consolidada de conciliações com filtros de carteira e responsável.
						</p>
					</div>
				</div>

				<FiltersCard
					title="Filtros"
					columns={2}
					actions={(
						<>
							<Button onClick={handleClearFilters} variant="secondary">
								Limpar
							</Button>
							<Button onClick={handleSearch} variant="primary">
								Buscar
							</Button>
						</>
					)}
				>
					<Input
						label="Carteira"
						value={filtros.carteira}
						onChange={(value) => handleFilterChange('carteira', value)}
						placeholder="Informe a carteira"
					/>
					<Input
						label="Responsável"
						value={filtros.responsavel}
						onChange={(value) => handleFilterChange('responsavel', value)}
						placeholder="Informe o responsável"
					/>
				</FiltersCard>

				{error && (
					<div className="error-message">
						<p>Erro: {error}</p>
					</div>
				)}

				{!loading && data.length > 0 && (
					<div className="conciliacoes-toolbar">
						<span className="conciliacoes-selection-count">
							{selectedKeys.length} de {MAX_CONCILIACOES_SELECIONADAS} selecionada(s)
						</span>
						<Button
							variant="primary"
							onClick={handleConciliarSelecionadas}
							disabled={selectedKeys.length === 0 || isLoadingSelection}
						>
							{isLoadingSelection
								? 'Iniciando conciliação...'
								: isLoadedSelection
									? 'Abrir conciliação'
									: 'Conciliar selecionadas'}
						</Button>
					</div>
				)}

				<DataTable<ConciliacaoItem>
					data={paginatedData}
					columns={[selectColumn, ...columns]}
					loading={loading}
						onSort={handleSort}
					sortField={sortField}
					sortDirection={sortDirection}
					rowKey={(item, index) => `${item.idUltimaConciliacao || item.carteira || 'row'}-${index}`}
					rowClassName={(item) => (isSelected(item) ? 'conciliacao-row--selected' : '')}
					emptyTitle="Nenhuma conciliação encontrada"
					emptyMessage="Não encontramos resultados para os filtros informados."
					emptyHint="Ajuste os filtros e tente novamente."
				/>

				{!loading && totalElements > 0 && (
					<Pagination
						currentPage={currentPage}
						totalPages={totalPages}
						pageSize={pageSize}
						totalElements={totalElements}
						onPageChange={setCurrentPage}
						onPageSizeChange={(size) => {
							setPageSize(size);
							setCurrentPage(0);
						}}
					/>
				)}
			</div>
		</ErrorBoundary>
	);
};

export default ConciliacoesGerais;
